/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, { useEffect, useMemo, useState } from 'react';
import ServiceCard from './ServiceCard';
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';
import { fetchProductMenu } from '@/redux/products/thunk';
import { MenuItem } from '@/redux/products/types';
import { Skeleton } from 'antd';
import { asset } from '@/utils/runtimeOrg';

const FETCH_RETRY_LIMIT = 2;
const FETCH_TIMEOUT_MS = 12_000;

const safeLog = {
  error: (...a: any[]) => {
    try {
      console.error('[ServicesSection]', ...a);
    } catch {}
  },
  warn: (...a: any[]) => {
    try {
      console.warn('[ServicesSection]', ...a);
    } catch {}
  },
};

type ServicesProps = {
  cfg: {
    servicesSection?: {
      title?: string;
      description?: string;
    };
  };
};

export default function ServicesSection({ cfg }: ServicesProps) {
  const servicesCfg = cfg?.servicesSection;

  const dispatch = useAppDispatch();
  const {
    data: menuItemsRaw,
    status: menuStatus,
  } = (useAppSelector((s) => s.products.menu) as any) ?? {
    data: [],
    status: 'idle',
    error: null,
  };

  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (menuStatus !== 'idle') return;

    const controller = new AbortController();
    const timer = setTimeout(() => {
      controller.abort();
      safeLog.warn('menu fetch timed out');
    }, FETCH_TIMEOUT_MS);

    const p = dispatch(
      fetchProductMenu({ signal: controller.signal } as any)
    ) as any;

    if (typeof p?.unwrap === 'function') {
      p.unwrap().catch((err: any) => safeLog.error('menu fetch failed', err));
    } else {
      p?.catch?.((err: any) => safeLog.error('menu fetch failed', err));
    }

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [dispatch, menuStatus]);

  useEffect(() => {
    if (menuStatus !== 'failed') return;
    if (retryCount >= FETCH_RETRY_LIMIT) return;

    const id = setTimeout(() => {
      setRetryCount((c) => c + 1);
      const p = dispatch(fetchProductMenu({} as any)) as any;
      p?.catch?.((err: any) => safeLog.error('retry failed', err));
    }, 800 + retryCount * 600);

    return () => clearTimeout(id);
  }, [menuStatus, retryCount, dispatch]);

  const services = useMemo(() => {
    const list = Array.isArray(menuItemsRaw) ? menuItemsRaw : [];
    return list.map((n: any) => {
      const rawIcon = n?.display_icon || n?.icon || '';
      const normalized = rawIcon
        ? rawIcon.startsWith('/')
          ? rawIcon
          : `/${rawIcon}`
        : '';
      const imageSrc = normalized
        ? asset(normalized)
        : asset('/services/other_services.svg');

      return {
        id: String(n?.id ?? ''),
        title: n?.name ?? '',
        description: n?.description ?? '',
        imageSrc,
        subproducts: Array.isArray(n?.subproducts)
          ? (n.subproducts as MenuItem[])
          : [],
      };
    });
  }, [menuItemsRaw]);

  const [openServiceId, setOpenServiceId] = useState<string | number | null>(
    null
  );

  const isLoading = menuStatus === 'loading' || menuStatus === 'idle';
  const noData = services.length === 0 && !isLoading;

  if (!servicesCfg) {
    return (
      <section id="services" aria-label="Services" className="bg-[#FFF7E5] py-16 px-4">
        <div className="mx-auto max-w-6xl">
          <div className="mb-10">
            <div className="h-9 w-64 rounded bg-black/10 mb-3" />
            <div className="h-4 w-80 rounded bg-black/10" />
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={`sk-${i}`}
                className="flex min-h-[280px] flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <Skeleton.Image
                  active
                  style={{
                    width: '100%',
                    height: 140,
                    borderRadius: 12,
                  }}
                />
                <div className="mt-4 space-y-2">
                  <Skeleton active title={{ width: '70%' }} paragraph={false} />
                  <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 2, width: ['100%', '90%'] }}
                  />
                </div>
                <div className="mt-auto pt-4">
                  <Skeleton.Button
                    active
                    style={{ width: '100%', height: 40, borderRadius: 999 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="services"
      aria-labelledby="services-heading"
      aria-busy={isLoading ? 'true' : 'false'}
      className="bg-[#FFFCEE] py-16"
    >
      <div className="mx-auto container px-4">
        <header className="mb-10">
          <h2
            id="services-heading"
            className="font-inter text-3xl md:text-4xl font-semibold text-slate-900"
          >
            {servicesCfg.title || 'Nyandarua County Government Services'}
          </h2>
          <p className="mt-3 max-w-3xl text-lg md:text-xl text-slate-700">
            {servicesCfg.description ||
              'The Nyandarua Portal enables you to access and make payments for a wide range of services from the County Government. Please select a service below to proceed.'}
          </p>
        </header>

        {noData && (
          <div>
            <div
              className="mb-6 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700"
              role="status"
              aria-live="assertive"
            >
              No services are available at this time. Please try again later.
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={`sk-card-${i}`}
                className="flex min-h-[300px] flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <div className="w-full h-[140px] rounded-2xl bg-slate-200/70 animate-pulse" />

                <div className="mt-4 space-y-2">
                  <Skeleton active title={{ width: '70%' }} paragraph={false} />
                  <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 3, width: ['100%', '95%', '80%'] }}
                  />
                </div>

                <div className="mt-auto pt-4">
                  <Skeleton.Button
                    active
                    style={{ width: '100%', height: 40, borderRadius: 999 }}
                  />
                </div>
              </div>
            ))}</div>

          </div>
        )}

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {isLoading &&
            Array.from({ length: 8 }).map((_, i) => (
              <div
                key={`sk-card-${i}`}
                className="flex min-h-75 flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <div className="w-full h-35 rounded-2xl bg-slate-200/70 animate-pulse" />

                <div className="mt-4 space-y-2">
                  <Skeleton active title={{ width: '70%' }} paragraph={false} />
                  <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 3, width: ['100%', '95%', '80%'] }}
                  />
                </div>

                <div className="mt-auto pt-4">
                  <Skeleton.Button
                    active
                    style={{ width: '100%', height: 40, borderRadius: 999 }}
                  />
                </div>
              </div>
            ))}

          {!isLoading &&
            services.map((s) => (
              <ServiceCard
                key={s.id}
                serviceId={s.id}
                title={s.title}
                description={s.description}
                imageSrc={s.imageSrc || asset('/services/other_services.svg')}
                subproducts={s.subproducts as any}
                className="h-full"
                isOpen={openServiceId === s.id}
                onOpen={() => setOpenServiceId(s.id)}
                onClose={() =>
                  setOpenServiceId((cur) => (cur === s.id ? null : cur))
                }
              />
            ))}
        </div>
      </div>
    </section>
  );
}
